///
/// EarthBrowser
///
/// Created by Matt Giger on 11/17/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type Geometry } from "@stovokor/shader/Geometry"
import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"

export interface PolylineOptions {
	positions: vec3[]
	width?: number
	loop?: boolean
}

/**
 * Polyline geometry for rendering screen-space width lines
 * Based on the technique described in:
 * https://cesium.com/blog/2013/04/22/robust-polyline-rendering-with-webgl/
 *
 * Each position is duplicated 4 times to form screen-aligned quads.
 * Adjacent positions are stored to compute proper miter joins.
 */
export class PolylineGeometry implements Geometry {
	// Current vertex position
	public position: VertexAttribute

	// Previous vertex position (for computing joins)
	public previousPosition: VertexAttribute

	// Next vertex position (for computing joins)
	public nextPosition: VertexAttribute

	// Extrusion side and quad end
	// Encoded as: sign is direction (-1 or 1), magnitude is which end of its
	// quad the vertex is (1 = leading, 2 = trailing)
	public direction: VertexAttribute

	// Width of the line in pixels
	public lineWidth: VertexAttribute

	// Progress along the line (0 at start, 1 at end)
	public lineProgress: VertexAttribute

	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer

	public mode: DrawMode = DrawMode.Triangles
	public available: boolean = true
	public bounds: cube
	public boundingSphere: sphere

	private _width: number
	private _positions: vec3[]
	private _loop: boolean
	private context: StovokorContext

	constructor(context: StovokorContext, options: PolylineOptions) {
		this.context = context
		this.position = new VertexAttribute(context, 3)
		this.previousPosition = new VertexAttribute(context, 3)
		this.nextPosition = new VertexAttribute(context, 3)
		this.direction = new VertexAttribute(context, 1)
		this.lineWidth = new VertexAttribute(context, 1)
		this.lineProgress = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [
			this.position,
			this.previousPosition,
			this.nextPosition,
			this.direction,
			this.lineWidth,
			this.lineProgress
		])
		this._positions = options.positions
		this._width = options.width ?? 2.0
		this._loop = options.loop ?? false

		if(this._positions.length < 2) {
			throw new Error("Polyline requires at least 2 positions")
		}

		this.buildPolyline(this._loop, true)

		// Recalculate bounds after building geometry
		this.position.calculateBounds()
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	private buildPolyline(isLoop: boolean, buildIndices: boolean): void {
		const positions = this._positions
		const segmentCount = isLoop ? positions.length : positions.length - 1

		// Calculate cumulative distances for line progress
		const distances: number[] = [0]
		let totalDistance = 0
		for(let i = 0; i < segmentCount; i++) {
			const curr = positions[i]
			const next = positions[(i + 1) % positions.length]
			totalDistance += curr.subtract(next).length
			distances.push(totalDistance)
		}

		// For each segment, we create 4 vertices (2 on each end of the segment)
		// to form a quad that will be screen-aligned
		for(let i = 0; i < segmentCount; i++) {
			const curr = positions[i]
			const next = positions[(i + 1) % positions.length]

			// Calculate progress along line (0 to 1)
			const currProgress = totalDistance > 0 ? distances[i] / totalDistance : 0
			const nextProgress = totalDistance > 0 ? distances[i + 1] / totalDistance : 1

			// Get previous and next positions for join computation. At line
			// endpoints the missing neighbor duplicates the vertex itself,
			// which the shader detects and extrudes perpendicular to the one
			// real segment instead of mitering.
			const prev = i === 0 && !isLoop
				? curr
				: positions[(i - 1 + positions.length) % positions.length]
			const nextNext = i === segmentCount - 1 && !isLoop
				? next
				: positions[(i + 2) % positions.length]

			// Create 4 vertices for this segment
			// Two leading vertices at current position (extruded in opposite directions)
			this.addVertex(curr, prev, next, -1, false, currProgress)
			this.addVertex(curr, prev, next, 1, false, currProgress)

			// Two trailing vertices at next position (extruded in opposite directions)
			this.addVertex(next, curr, nextNext, -1, true, nextProgress)
			this.addVertex(next, curr, nextNext, 1, true, nextProgress)

			// Create indices for the quad (2 triangles)
			// Winding order adjusted for screen-space extrusion
			if(buildIndices) {
				const baseIndex = i * 4
				this.indexBuffer.quad(
					baseIndex + 0,
					baseIndex + 2,
					baseIndex + 3,
					baseIndex + 1
				)
			}
		}
	}

	private addVertex(
		pos: vec3,
		prev: vec3,
		next: vec3,
		dir: number,
		trailing: boolean,
		progress: number
	): void {
		// Current position
		this.position.addVec(pos)

		// Previous position (for computing miter join at this vertex)
		this.previousPosition.addVec(prev)

		// Next position (for computing miter join at this vertex)
		this.nextPosition.addVec(next)

		// Direction to extrude (-1 = one side, 1 = other side); the magnitude
		// marks which end of its quad this vertex is (1 = leading, 2 =
		// trailing) so the shader near-plane-clips against the quad's own
		// segment (toward next when leading, toward prev when trailing)
		this.direction.add(dir * (trailing ? 2.0 : 1.0))

		// Line width in pixels
		this.lineWidth.add(this._width)

		// Progress along line (0 to 1)
		this.lineProgress.add(progress)
	}

	/**
	 * Update the polyline positions
	 *
	 * When the position count and loop mode are unchanged, the vertex data is
	 * refilled in place and re-uploaded into the existing GPU buffer on the
	 * next bind (indices are unchanged). This path is cheap enough to run
	 * every frame for animated lines.
	 *
	 * @returns true if the vertex/index buffers were recreated (callers that
	 * captured buffer references, e.g. renderers, must be rebuilt)
	 */
	public updatePositions(positions: vec3[], loop: boolean = false): boolean {
		const sameLayout = positions.length === this._positions.length && loop === this._loop
		this._positions = positions
		this._loop = loop

		// Clear existing data
		this.position.values = []
		this.previousPosition.values = []
		this.nextPosition.values = []
		this.direction.values = []
		this.lineWidth.values = []
		this.lineProgress.values = []

		if(sameLayout) {
			// Fast path: refill vertex data, keep the same buffers and indices.
			// VertexBuffer.bind() re-uploads whenever attributes hold values.
			this.buildPolyline(loop, false)
			this.bounds = this.position.bounds
			this.boundingSphere = this.bounds.sphere
			return false
		}

		this.indexBuffer.destroy()
		this.indexBuffer = new IndexBuffer(this.context)

		// Rebuild
		this.buildPolyline(loop, true)

		// Update vertex buffer
		this.vertexBuffer.destroy()
		this.vertexBuffer = new VertexBuffer(this.context, [
			this.position,
			this.previousPosition,
			this.nextPosition,
			this.direction,
			this.lineWidth,
			this.lineProgress
		])

		// Recalculate bounds after rebuilding
		this.position.calculateBounds()
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
		return true
	}

	/**
	 * Update the line width
	 */
	public updateWidth(width: number): void {
		this._width = width
		for(let i = 0; i < this.lineWidth.values.length; i++) {
			this.lineWidth.values[i] = width
		}
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
